import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import uuid from 'uuid';

import { Button } from '@amzn/stencil-react-components/button';
import { Col, Row, View } from '@amzn/stencil-react-components/layout';
import { H4, Text } from '@amzn/stencil-react-components/text';

import BorderedView from '../common/BorderedView';
import { IconHeading } from '../common/CustomIcon';
import { IconBodyText } from '../atoms/custom-icon/IconBodyText';
import { IconBulletList } from '../atoms/custom-icon/IconBulletList';
import ImageUploader from '../molecules/ImageUploader';
import { IconFolder } from '../atoms/custom-icon/IconFolder';
import { FormattedMessage, useIntl } from 'react-intl';
import TemplateList from './TemplateList';
import { updateMessageContent } from '../../actions/nudgeActions';
import { SECTION } from '../../constants/editorConfig';
import { templateContentSelector } from '../../selectors/selectors';

export const ElementsPanel = () => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const elements = useSelector(templateContentSelector);
  const [displayTemplateList, setDisplayTemplateList] = useState(false);

  const openTemplateList = () => {
    setDisplayTemplateList(!displayTemplateList);
  };

  const addMessageSection = sectionName => {
    const hasElements = Array.isArray(elements);
    const updatedContentElements = hasElements ? [...elements] : [];
    updatedContentElements.push({
      id: uuid.v4(),
      type: sectionName,
      content: SECTION[sectionName] ?? '',
    });
    dispatch(updateMessageContent(updatedContentElements));
  };

  return (
    <>
      <View flexDirection="column" width="100%">
        <H4>
          <FormattedMessage id="app.elementPanel.label.elements" />
        </H4>
        <BorderedView
          justifyContent="center"
          padding={'10px 16px'}
          gridGap="S200"
          flexDirection="column"
        >
          <Col
            backgroundColor="greyBg"
            height={'100%'}
            gridGap="S200"
            display="flex"
            flexDirection="row"
          >
            <Row flexWrap="down" gridGap="S200" justifyContent="space-between">
              <View width={'49%'} justifyContent="center" alignItems="center">
                <Button
                  data-testid="headerBtn"
                  secondary
                  display="flex"
                  flexDirection="column"
                  style={{ borderRadius: '3px', height: '60px', width: '130px' }}
                  onClick={() => addMessageSection('header')}
                >
                  <IconHeading size={20} />
                  <Text>
                    <FormattedMessage id="app.elementPanel.label.heading" />
                  </Text>
                </Button>
              </View>
              <View width={'49%'} justifyContent="center" alignItems="center">
                <Button
                  data-testid="bodyText"
                  secondary
                  display="flex"
                  flexDirection="column"
                  style={{ borderRadius: '3px', height: '60px', width: '130px' }}
                  onClick={() => addMessageSection('bodyText')}
                >
                  <IconBodyText size={22} />
                  <Text>
                    <FormattedMessage id="app.elementPanel.label.bodyText" />
                  </Text>
                </Button>
              </View>
            </Row>

            <Row flexWrap="down" gridGap="S200" justifyContent="space-between">
              <View width={'49%'} justifyContent="center" alignItems="center">
                <Button
                  data-testid="bulletBtn"
                  display="flex"
                  flexDirection="column"
                  secondary
                  style={{ borderRadius: '3px', height: '60px', width: '130px' }}
                  onClick={() => addMessageSection('bullet')}
                >
                  <IconBulletList size={20} />
                  <Text>
                    <FormattedMessage id="app.elementPanel.label.bullet" />
                  </Text>
                </Button>
              </View>
              <View width={'49%'} justifyContent="center" alignItems="center">
                <Button style={{ borderRadius: '3px', height: '60px', width: '130px' }} secondary>
                  <ImageUploader
                    title={formatMessage({
                      id: 'app.elementPanel.label.addImage',
                      defaultMessage: 'Add Image',
                    })}
                  />
                </Button>
              </View>
            </Row>

            <Row flexWrap="down" gridGap={'S200'}>
              <View display="flex" justifyContent="center" alignItems="center" flex={1}>
                <Button
                  secondary
                  onClick={openTemplateList}
                  display="flex"
                  flexDirection="column"
                  data-testid="templateBtn"
                  style={{ borderRadius: '3px', height: '60px' }}
                >
                  <IconFolder size={20} />
                  <Text>
                    <FormattedMessage
                      id="app.createContent.useTemplate"
                      defaultMessage="Use existing templates"
                    />
                  </Text>
                </Button>
                <TemplateList
                  isOpen={displayTemplateList}
                  close={() => setDisplayTemplateList(false)}
                />
              </View>
            </Row>
          </Col>
        </BorderedView>
      </View>
    </>
  );
};
